import { useRef, useState } from "react";
import BaseDialog from "../modelDialogs/BaseDialog";
import type BaseModelReference from "../models/BaseModelReference";
import type Device from "../models/Device";
import { itx } from "../translationItx/itx";
import { isUiModeDeveloper } from "../uiMode";
import { findSimilarProperties, setValueForSimilarProperties } from "../KnxProductDataTools/similarProperties";

type SimilarProperty = {
  id: string;
  instanceId: string;
};

function getSimilarPropertyRowCheckboxId(similarProperty: SimilarProperty, deviceId: string): string {
  return `device_${deviceId}_property_${similarProperty.id}_${similarProperty.instanceId}`;
}

function updateDeviceParentCheckbox(deviceId: string): void {
  const childCheckboxes = document.querySelectorAll(
    `input[data-device-id="${deviceId}"]`,
  ) as NodeListOf<HTMLInputElement>;
  const deviceParent = document.querySelector(`input[data-device-parent="${deviceId}"]`) as HTMLInputElement | null;

  if (!deviceParent || childCheckboxes.length === 0) return;

  const checkedCount = Array.from(childCheckboxes).filter((cb) => cb.checked).length;
  if (checkedCount === childCheckboxes.length) {
    deviceParent.indeterminate = false;
    deviceParent.checked = true;
  } else if (checkedCount === 0) {
    deviceParent.indeterminate = false;
    deviceParent.checked = false;
  } else {
    deviceParent.indeterminate = true;
    deviceParent.checked = false;
  }
}

function updateGlobalParentCheckbox(): void {
  const deviceParents = document.querySelectorAll(`input[data-device-parent]`) as NodeListOf<HTMLInputElement>;
  const globalParent = document.getElementById("global_parent_all_props") as HTMLInputElement | null;

  if (!globalParent || deviceParents.length === 0) return;

  const checkedCount = Array.from(deviceParents).filter((cb) => cb.checked && !cb.indeterminate).length;
  const indeterminateCount = Array.from(deviceParents).filter((cb) => cb.indeterminate).length;

  if (indeterminateCount > 0 || (checkedCount > 0 && checkedCount < deviceParents.length)) {
    globalParent.indeterminate = true;
    globalParent.checked = false;
  } else if (checkedCount === deviceParents.length) {
    globalParent.indeterminate = false;
    globalParent.checked = true;
  } else {
    globalParent.indeterminate = false;
    globalParent.checked = false;
  }
}

function DeviceElement(props: {
  device: Device;
  isCurrentDevice: boolean;
  similarProps: SimilarProperty[];
  selectedSimilarPropsRef: React.MutableRefObject<Record<string, boolean>>;
  newValue: string | undefined;
  newValueDisplayText: string;
  valueList: Array<{ key: string; value: string; valueEN: string | undefined }>;
}) {
  const { device, isCurrentDevice, similarProps, selectedSimilarPropsRef, newValue, newValueDisplayText, valueList } =
    props;

  const deviceHeader = isCurrentDevice
    ? itx("KnxPropertyViewAdminButton.thisDevice", "Dieses Gerät")
    : `${device.fullTag} ${device.name}`;

  return (
    <>
      <tr
        style={{
          backgroundColor: "lightgrey",
          borderRadius: " 4px 4px 0 0",
          borderBottom: "1px solid #ddd",
        }}
      >
        <td
          style={{
            width: "32px",
            paddingLeft: "10px",
            paddingTop: "6px",
            paddingBottom: "6px",
            verticalAlign: "middle",
          }}
        >
          <input
            type="checkbox"
            data-device-parent={device.id}
            defaultChecked={true}
            onChange={(ev) => {
              const checked = ev.target.checked;
              const childCheckboxes = document.querySelectorAll(
                `input[data-device-id="${device.id}"]`,
              ) as NodeListOf<HTMLInputElement>;
              childCheckboxes.forEach((cb) => {
                cb.checked = checked;
                selectedSimilarPropsRef.current[cb.id] = checked;
              });
              updateGlobalParentCheckbox();
            }}
          />
        </td>
        <td colSpan={4} style={{ paddingTop: "6px", paddingBottom: "6px", fontSize: "13px", fontWeight: 600 }}>
          {deviceHeader}
        </td>
      </tr>

      {similarProps.map((similarPropertiy) => {
        const checkboxId = getSimilarPropertyRowCheckboxId(similarPropertiy, device.id);
        const info = device.knxInstanceData.getExtendedData(similarPropertiy.id, similarPropertiy.instanceId)?.info;
        const currentOtherValue = device.knxInstanceData.getValue(similarPropertiy.id, similarPropertiy.instanceId);

        if (currentOtherValue == undefined) return null;

        if (selectedSimilarPropsRef.current[checkboxId] == undefined) {
          selectedSimilarPropsRef.current[checkboxId] = true;
        }

        const currentValueItem = valueList?.find((item) => item.key == currentOtherValue);
        const currentValueDisplayText = currentValueItem?.value || (valueList?.length == 0 ? currentOtherValue : "???");
        const unchanged = currentOtherValue == newValue;

        return (
          <tr
            key={`${device.id}_${similarPropertiy.id}_${similarPropertiy.instanceId}`}
            style={{ backgroundColor: unchanged ? "#fafafa" : "white" }}
          >
            <td style={{ width: "32px", paddingLeft: "10px", verticalAlign: "middle" }}>
              <input
                type="checkbox"
                id={checkboxId}
                data-similar-key={checkboxId}
                data-device-id={device.id}
                defaultChecked={selectedSimilarPropsRef.current[checkboxId] ?? true}
                onChange={(ev) => {
                  selectedSimilarPropsRef.current[checkboxId] = ev.target.checked;
                  updateDeviceParentCheckbox(device.id);
                  updateGlobalParentCheckbox();
                }}
              />
            </td>
            <td style={{ paddingLeft: "24px", paddingTop: "4px", paddingBottom: "4px", fontSize: "12px" }}>
              <span style={{ color: "#888" }}>{info?.chapterName}</span>
              {" / "}
              <span style={{ color: "#333" }}>{info?.textEN || "???"}</span>
            </td>
            <td style={{ padding: "4px 8px", fontSize: "12px" }}>
              <span style={{ color: unchanged ? "#aaa" : "#333" }}>
                {
                  currentValueDisplayText // no itx
                }
                {
                  " -> " // no itx
                }
                {newValueDisplayText}
                {unchanged && (
                  <span style={{ marginLeft: "6px", color: "#bbb", fontStyle: "italic" }}>
                    {itx("knxPropertyViewAdminButton.noChange", "keine Aenderung")}
                  </span>
                )}
              </span>
            </td>
            {isUiModeDeveloper() && (
              <td style={{ color: "#aaa", fontSize: "11px", padding: "0 6px" }}>
                {similarPropertiy.id} / {similarPropertiy.instanceId}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

export default function ApplyParameterToAllDialog(props: {
  device: Device;
  instanceId: string;
  id: string;
  valueList: Array<{ key: string; value: string; valueEN: string | undefined }>;
}) {
  const { device, instanceId, id, valueList } = props;

  const [applyToAllDevices, setApplyToAllDevices] = useState(false);
  const [applyToAllChannels, setApplyToAllChannels] = useState(true);
  const selectedSimilarPropsRef = useRef<Record<string, boolean>>({});

  const similarProps: SimilarProperty[] = applyToAllChannels
    ? findSimilarProperties(device, id, instanceId)
    : [{ id, instanceId }];

  const newValue = device.knxInstanceData.getValue(id, instanceId);
  const newValueDisplayText =
    valueList?.find((item) => item.key == newValue)?.value || (valueList?.length == 0 ? newValue : "???");

  let allDevices = new Array<Device>();
  let allDeviceRefs = new Array<BaseModelReference>();
  allDeviceRefs = device.template?.getUsages() || [];

  if (applyToAllDevices) {
    for (const deviceRef of allDeviceRefs) {
      if (deviceRef.referencedObject) {
        allDevices.push(deviceRef.referencedObject as Device);
      }
    }
  } else {
    allDevices = [device];
  }

  return (
    <BaseDialog
      title={itx("knxPropertyViewAdminButton.applyForAll", "Auf Alle anwenden") + ": " + newValueDisplayText}
      enterAsOk={true}
      onOk={() => {
        if (!applyToAllDevices) {
          allDevices = [device];
        }
        allDevices.forEach((dev) => {
          const selectedPropsForDevice = similarProps.filter((similarProperty) => {
            const cbId = getSimilarPropertyRowCheckboxId(similarProperty, dev.id);
            const cb = document.getElementById(cbId) as HTMLInputElement | null;
            return cb ? cb.checked : (selectedSimilarPropsRef.current[cbId] ?? true);
          });
          setValueForSimilarProperties(dev, selectedPropsForDevice, newValue);
        });

        return true;
      }}
      onClose={() => true}
    >
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", padding: "6px 0 10px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
          <input type="checkbox" onChange={(ev) => setApplyToAllDevices(ev.target.checked)} />
          {itx("knxPropertyViewAdminButton.forAllDevices", "Fuer alle Geraete")}{" "}
          {allDeviceRefs.length > 1
            ? `(${allDeviceRefs.length})`
            : `(${itx("knxPropertyViewAdminButton.noOtherUsage", "kein anderes Geraet verwendet dieses Produkt")})`}
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
          <input type="checkbox" defaultChecked={true} onChange={(ev) => setApplyToAllChannels(ev.target.checked)} />
          {itx("knxPropertyViewAdminButton.allChannels", "Fuer alle Kanaele")}
        </label>
      </div>

      <div style={{ border: "1px solid #ddd", borderRadius: "4px", overflow: "hidden" }}>
        <div
          style={{
            backgroundColor: "var(--backgroundColorGrey)",
            color: "var(--white1)",
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          <input
            type="checkbox"
            id="global_parent_all_props"
            defaultChecked={true}
            onChange={(ev) => {
              const checked = ev.target.checked;
              const allPropertyCheckboxes = document.querySelectorAll(
                `input[data-device-id]`,
              ) as NodeListOf<HTMLInputElement>;
              allPropertyCheckboxes.forEach((cb) => {
                cb.checked = checked;
                selectedSimilarPropsRef.current[cb.id] = checked;
              });
              const deviceParentCheckboxes = document.querySelectorAll(
                `input[data-device-parent]`,
              ) as NodeListOf<HTMLInputElement>;
              deviceParentCheckboxes.forEach((cb) => {
                cb.checked = checked;
                cb.indeterminate = false;
              });
            }}
          />
          {itx("knxPropertyViewAdminButton.allProperties", "Alle Properties")}
        </div>

        <div style={{ maxHeight: "380px", overflow: "auto" }}>
          <table style={{ width: "100%", minWidth: "400px", borderCollapse: "collapse" }}>
            <tbody>
              {allDevices.map((dev) => (
                <DeviceElement
                  key={dev.id}
                  device={dev}
                  isCurrentDevice={dev === device}
                  similarProps={similarProps}
                  selectedSimilarPropsRef={selectedSimilarPropsRef}
                  newValue={newValue}
                  newValueDisplayText={newValueDisplayText}
                  valueList={valueList}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </BaseDialog>
  );
}
